/** @jsxImportSource @emotion/react */
/* eslint-disable react/no-array-index-key */
import React, { useState, useRef, useMemo, ReactNode, useContext, useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { css } from "@emotion/react";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Paper from "@material-ui/core/Paper";
import SvgIcon from "@material-ui/core/SvgIcon";
import Typography from "@material-ui/core/Typography";
import { some } from "lodash";
import { v4 as uuidv4 } from "uuid";

import { ReactComponent as IcnArrowUp } from "../assets/images/arrow-icn-arrow-small-up.svg";
import { ReactComponent as IcnCross } from "../assets/images/icn-cross.svg";
import { ReactComponent as IcnRefresh } from "../assets/images/icon-icn-refresh.svg";
import { ReactComponent as IcnLineHorizontal } from "../assets/images/icon-line-horizontal.svg";
import ProgressBar, { PROGRESS_STATUS } from "../components/ProgressBar";
import theme from "../styles/theme";
import { blue, green, red, greyDark, greySecondary, white } from "../styles/variables";
import MultipartUploader from "../utils/MultipartUploader";

import { useModal } from "./modal";

const formatToFileSize = (value: number, decimals = 2) => {
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  if (value === 0) return "0 Byte";
  const i = Math.floor(Math.log(value) / Math.log(1024));
  return `${parseFloat((value / 1024 ** i).toFixed(decimals))} ${sizes[i]}`;
};

const useStyles = () => ({
  dialogContainer: css`
    .MuiDialog-container {
      position: fixed;
      height: unset;
      bottom: 80px;
      right: 0;
    }
  `,
  dialogPaper: css`
    .MuiDialog-paper {
      width: 480px;
      margin: ${theme.spacing(4)}px;
    }
  `,
  dialogTitle: css`
    background: ${blue};
    padding: ${theme.spacing(3, 4)}px;
    color: ${white};
    font-weight: bold;
  `,
  icnArrowUp: css`
    width: 16px;
    position: absolute;
    right: 52px;
    cursor: pointer;
    & g {
      fill: ${white};
    }
  `,
  icnClose: css`
    width: 16px;
    position: absolute;
    right: 16px;
    cursor: pointer;
    & g {
      fill: ${white};
    }
  `,
  dialogContent: css`
    padding: 0;
    max-height: 416px;
  `,
  item: css`
    padding: 20px;
    &:not(:last-of-type) {
      border-bottom: 1px solid ${greySecondary};
    }
  `,
  itemTitle: css`
    display: flex;
    align-items: center;
    margin-bottom: ${theme.spacing(2)}px;
  `,
  filename: css`
    font-weight: bold;
    word-wrap: break-word;
    word-break: break-all;
    overflow: hidden;
  `,
  icnRemove: css`
    width: 16px;
    height: 16px;
    margin-left: auto;
    cursor: pointer;
  `,
  details: css`
    display: flex;
    margin-top: ${theme.spacing(2)}px;
  `,
  status: css`
    margin-right: ${theme.spacing(2)}px;
    font-weight: bold;
    color: ${blue};
  `,
  succeedStatus: css`
    color: ${green};
  `,
  failedStatus: css`
    color: ${red};
  `,
  fileInfo: css`
    margin-right: ${theme.spacing(2)}px;
    color: ${greyDark};
  `,
  modalContent: css`
    padding: 0 24px;
    height: 100px;
  `,
});

// eslint-disable-next-line no-shadow
export enum UPLOAD_STATE {
  READY,
  UPLOADING,
  COMPLETED,
  ERROR,
}

interface InfoProps {
  id: string;
  status: UPLOAD_STATE;
  file: File;
  progress?: number;
  loadedSize?: number;
  uploader?: MultipartUploader;
}

type UploadToS3Props = {
  files: Array<File>;
};

interface Schema {
  uploadToS3: (value: UploadToS3Props) => Promise<void>;
}

interface ProviderProps {
  children: ReactNode;
}

export interface UploadContextValue {
  schema: Schema;
}

const UploadContext = React.createContext<UploadContextValue>({
  schema: {} as Schema,
});

const UploadContextProvider = ({ children }: ProviderProps) => {
  const intl = useIntl();
  const classes = useStyles();
  const [info, setInfo] = useState<InfoProps[]>([]);
  const [showContent, setShowContent] = useState(true);
  const [showModal, hideModal] = useModal();

  const uploadFileToBlob = (file: File, id: string) => {
    const uploader = new MultipartUploader({
      file,
      onProgress: (progress: number) => {
        setInfo((prevState) => {
          const copyInfo = [...prevState];
          const matchedIndex = copyInfo.findIndex((item) => item.status === UPLOAD_STATE.UPLOADING && item.id === id);
          if (matchedIndex > -1) {
            const matchedFile = {
              ...copyInfo[matchedIndex],
              progress,
              loadedSize: copyInfo[matchedIndex].file.size * (progress / 100),
            };
            copyInfo[matchedIndex] = matchedFile;
          }
          return copyInfo;
        });
      },
      onComplete: () => {
        setInfo((prevState) => {
          const copyInfo = [...prevState];
          const matchedIndex = copyInfo.findIndex((item) => item.status === UPLOAD_STATE.UPLOADING && item.id === id);
          if (matchedIndex > -1) {
            const matchedFile = {
              ...copyInfo[matchedIndex],
              progress: 100,
              status: UPLOAD_STATE.COMPLETED,
            };
            copyInfo[matchedIndex] = matchedFile;
          }
          return copyInfo;
        });
      },
      onError: (error: Error) => {
        setInfo((prevState) => {
          const copyInfo = [...prevState];
          const matchedIndex = copyInfo.findIndex((item) => item.id === id);
          if (matchedIndex > -1) {
            const matchedFile = { ...copyInfo[matchedIndex] };
            matchedFile.status = UPLOAD_STATE.ERROR;
            copyInfo[matchedIndex] = matchedFile;
          }
          return copyInfo;
        });
      },
    });

    uploader.upload();

    return uploader;
  };

  const getInitialInfoItem = (file: File): InfoProps => {
    const id = uuidv4();
    return {
      id,
      file,
      status: UPLOAD_STATE.READY,
      progress: 0,
      loadedSize: 0,
    };
  };

  const schemaRef = useRef({
    uploadToS3: async (value: UploadToS3Props) => {
      const { files } = value;
      const newInfoItems = files.map(getInitialInfoItem);
      // new uploads should be placed in front of previous uploads
      setInfo((prevInfoItems) => [...newInfoItems, ...prevInfoItems]);
    },
  });

  useEffect(() => {
    const hasReadyUploads = some(info, { status: UPLOAD_STATE.READY });
    if (hasReadyUploads) {
      setInfo((prevInfoItems) => {
        const copyInfo = [...prevInfoItems];
        return copyInfo.map((infoItem) => {
          if (infoItem.status === UPLOAD_STATE.READY) {
            const { file, id } = infoItem;
            const uploader = uploadFileToBlob(file, id);

            return {
              ...infoItem,
              uploader,
              status: UPLOAD_STATE.UPLOADING,
            };
          }
          return infoItem;
        });
      });
    }
  }, [info]);

  useEffect(() => {
    const hasFileUploading = some(info, { status: UPLOAD_STATE.UPLOADING });
    if (hasFileUploading) {
      window.onbeforeunload = () => intl.formatMessage({ id: "file.upload-cancel-all.modal.content" });
    }
    return () => {
      window.onbeforeunload = null;
    };
  }, [info, intl]);

  const clearUploaders = () => {
    info.forEach((infoItem) => {
      if (infoItem.uploader) {
        infoItem.uploader.abort();
      }
    });
  };

  const closeModal = () => {
    const isNoneUploading = !some(info, { status: UPLOAD_STATE.UPLOADING });
    if (isNoneUploading) {
      hideModal();
      clearUploaders();
      setInfo([]);
    } else {
      showModal({
        title: intl.formatMessage({ id: "file.upload-cancel-all.modal.title" }),
        content: (
          <span css={classes.modalContent}>{intl.formatMessage({ id: "file.upload-cancel-all.modal.content" })}</span>
        ),
        actions: [
          {
            label: intl.formatMessage({
              id: "file.upload-cancel-all.btn.back",
            }),
            color: "white",
            onClick: (): void => {
              hideModal();
            },
          },
          {
            label: intl.formatMessage({
              id: "file.upload-cancel-all.btn.cancel",
            }),
            color: "error",
            onClick: async (): Promise<void> => {
              hideModal();
              clearUploaders();
              setInfo([]);
            },
          },
        ],
      });
    }
  };

  const toggleHideContent = () => {
    setShowContent((prev) => !prev);
  };

  const handleClickRemove = (index: number) => {
    showModal({
      title: intl.formatMessage({ id: "file.upload-cancel.modal.title" }),
      content: <span css={classes.modalContent}>{intl.formatMessage({ id: "file.upload-cancel.modal.content" })}</span>,
      actions: [
        {
          label: intl.formatMessage({ id: "file.upload-cancel.btn.back" }),
          color: "white",
          onClick: (): void => {
            hideModal();
          },
        },
        {
          label: intl.formatMessage({ id: "file.upload-cancel.btn.cancel" }),
          color: "error",
          onClick: async (): Promise<void> => {
            hideModal();
            setInfo((prev) => {
              const copyPrev = [...prev];
              const uploader = copyPrev[index] && copyPrev[index].uploader;
              if (uploader) {
                uploader.abort();
              }
              const startedFiles = copyPrev.slice(0, index);
              const endedFiles = copyPrev.slice(index + 1, copyPrev.length);
              return [...startedFiles, ...endedFiles];
            });
          },
        },
      ],
    });
  };

  const handleClickRetry = (index: number) => {
    setInfo((prevInfoItems) => {
      const copyInfo = [...prevInfoItems];
      const newInfoItem = {
        ...copyInfo[index],
        status: UPLOAD_STATE.READY,
      };
      copyInfo[index] = newInfoItem;
      return copyInfo;
    });
  };

  const getProgressBarStatus = (uploadStatus: UPLOAD_STATE) => {
    if (uploadStatus === UPLOAD_STATE.ERROR) {
      return PROGRESS_STATUS.FAILED;
    }
    if (uploadStatus === UPLOAD_STATE.COMPLETED) {
      return PROGRESS_STATUS.COMPLETED;
    }
    return PROGRESS_STATUS.PROCESSING;
  };

  // eslint-disable-next-line
  const value = useMemo(() => ({ schema: schemaRef.current }), [info]);
  return (
    <UploadContext.Provider value={value}>
      <Dialog
        open={info.length > 0}
        onClose={closeModal}
        scroll="paper"
        hideBackdrop
        disableBackdropClick
        disableEnforceFocus
        disableEscapeKeyDown
        disableScrollLock
        css={[classes.dialogContainer, classes.dialogPaper]}
        style={{ position: "relative" }}
      >
        <DialogTitle disableTypography css={classes.dialogTitle}>
          <FormattedMessage id="file.upload.modal.title" />
          <SvgIcon
            component={showContent ? IcnLineHorizontal : IcnArrowUp}
            viewBox="0 0 16 16"
            css={classes.icnArrowUp}
            data-test={showContent ? "show-modal-content-button" : "hide-modal-content-button"}
            onClick={toggleHideContent}
          />
          <SvgIcon
            component={IcnCross}
            viewBox="0 0 20 20"
            css={classes.icnClose}
            data-test="close-modal-button"
            onClick={closeModal}
          />
        </DialogTitle>
        {showContent && (
          <DialogContent css={classes.dialogContent}>
            {info &&
              info.map((infoItem, idx: number) => {
                const { file, status, progress = 0, loadedSize = 0 } = infoItem;
                const fileSize = file.size || 0;
                const isFinished = progress === 100;
                const formatFileSize = formatToFileSize(fileSize);
                const formatLoadedSize = formatToFileSize(loadedSize);
                const progressBarStatus = getProgressBarStatus(status);
                return (
                  <Paper elevation={0} key={idx} css={classes.item}>
                    <div css={classes.itemTitle}>
                      <Typography css={classes.filename}>{file.name}</Typography>
                      {status === UPLOAD_STATE.UPLOADING && (
                        <SvgIcon
                          component={IcnCross}
                          viewBox="0 0 16 16"
                          css={classes.icnRemove}
                          data-test={`remove-button-${idx}`}
                          onClick={() => handleClickRemove(idx)}
                        />
                      )}
                      {status === UPLOAD_STATE.ERROR && (
                        <SvgIcon
                          component={IcnRefresh}
                          viewBox="0 0 16 16"
                          css={classes.icnRemove}
                          data-test={`remove-button-${idx}`}
                          onClick={() => handleClickRetry(idx)}
                        />
                      )}
                    </div>
                    <ProgressBar status={progressBarStatus} percentage={progress} />
                    <div css={classes.details}>
                      {status === UPLOAD_STATE.ERROR ? (
                        <>
                          <Typography css={[classes.status, classes.failedStatus]}>
                            <FormattedMessage id="file.upload.failed" />
                          </Typography>
                          <Typography css={classes.fileInfo}>
                            <FormattedMessage id="file.upload.failed.hint" />
                          </Typography>
                        </>
                      ) : (
                        <>
                          <Typography css={[classes.status, isFinished && classes.succeedStatus]}>
                            {isFinished ? <FormattedMessage id="file.upload.succeed" /> : `${progress}%`}
                          </Typography>
                          <Typography css={classes.fileInfo}>
                            <FormattedMessage
                              id="file.upload.uploaded"
                              values={{
                                loaded_size: isFinished ? formatFileSize : `${formatLoadedSize}/${formatFileSize}`,
                              }}
                            />
                          </Typography>
                        </>
                      )}
                    </div>
                  </Paper>
                );
              })}
          </DialogContent>
        )}
      </Dialog>
      {children}
    </UploadContext.Provider>
  );
};

const useUploadContext = (): UploadContextValue => useContext(UploadContext) as UploadContextValue;

export { UploadContextProvider, useUploadContext };
